/**
 *
 * FlexTable
 *
 */

import React, { useState, useRef, forwardRef, useEffect } from 'react';

import styles from './flex-table.module.css';
import { Icon } from 'semantic-ui-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { isMobileOnly } from 'react-device-detect';
import lodash from 'lodash';
import FlexBox from 'components/common/FlexBox';
import FlexLabel from 'components/common/FlexLabel';
import AnimatedLoader from 'components/AnimatedLoader';

function TableHeaderCell(props) {
  const { params, changeSortColumn, sortColumnKey, totalWeight, basic, forceMobile } = props;
  let stateBasedSortDirection = props.sortDirection;
  const {
    sortable,
    text,
    key,
    weight,
    basis,
    onClick,
    isSortColumn,
    sortDirection,
    justify,
    precontent,
    postcontent,
    component,
    minWidth,
    icon,
    material,
  } = params;
  let style = {};
  let contentStyle = {};
  if (weight) {
    style.flex = '0 0 ' + (weight / totalWeight) * 100 + '%';
  }
  if (basis) {
    if (!isMobileOnly) {
      style.flexBasis = basis + 'px';
    }
    style.flexGrow = 1;
  }

  if (isMobileOnly) {
    style.minWidth = '200px';
  } else {
    if (minWidth) {
      style.minWidth = minWidth + 'px';
    }
  }

  let headerTextStyle = {
    color: 'var(--text-main-high-emphasis)',
  };
  if (justify && !(isMobileOnly || forceMobile)) {
    contentStyle.justifyContent = justify;
    if (justify === 'center') {
      contentStyle.textAlign = 'center';
      headerTextStyle.textAlign = 'center';
    }
  }

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, params);
    }

    changeSortColumn(key);
  };

  let sortElem;
  let sortByThisColumn = false;
  let direction;
  if (isSortColumn) {
    sortByThisColumn = true;
  } else if (sortColumnKey === key) {
    sortByThisColumn = true;
  }

  if (sortDirection) {
    direction = sortDirection;
  } else if (stateBasedSortDirection) {
    direction = stateBasedSortDirection;
  }

  if (sortByThisColumn) {
    // Prefer controlled component
    if (direction) {
      if (direction === 'ascending') {
        sortElem = <Icon className={styles.columnSortIcon} name="caret up" fitted />;
      } else {
        sortElem = <Icon className={styles.columnSortIcon} name="caret down" fitted />;
      }
    }
  }

  if (sortable === false) {
    style.pointerEvents = 'none';
  }

  if (basic) {
    style.padding = 0;
    contentStyle.padding = 0;
    contentStyle.height = '100%';
    style.borderLeft = 'none';
    style.borderRight = 'none';
  }

  return (
    <div style={style} className={styles.tableHeaderCell}>
      {precontent}
      <div style={contentStyle} className={styles.tableHeaderCellStandardContent}>
        {component ? (
          component
        ) : (
          <FlexLabel
            textStyle={headerTextStyle}
            iconStyle={{ color: 'var(--background-color-darkest)', marginRight: 5, fontSize: 14 }}
            text={text}
            icon={icon}
            material={material}
          />
        )}
        {sortable !== false && sortElem}
        {sortable !== false && (
          <div onClick={handleClick} className={`${styles.headerCellSelectorElem} ${basic ? styles.basic : ''}`}></div>
        )}
      </div>
      {postcontent}
    </div>
  );
}

const TableHeader = forwardRef((props, ref) => {
  const { columns, sortColumnKey, changeSortColumn, sortDirection, totalWeight, basic, banner, forceMobile } = props;
  const headerColumnElems = columns.map((column) => {
    return (
      <TableHeaderCell
        forceMobile={forceMobile}
        key={column.key}
        basic={basic}
        totalWeight={totalWeight}
        params={column}
        sortColumnKey={sortColumnKey}
        changeSortColumn={changeSortColumn}
        sortDirection={sortDirection}
      />
    );
  });

  let style = {};
  if (isMobileOnly || forceMobile) {
    style.display = 'none';
  }

  if (basic) {
    style.background = 'none';
    style.padding = '0px';
    style.boxShadow = 'none';
  }

  return (
    <div style={style} className={styles.tableHeader}>
      {/* {banner && <div className={styles.tableBanner}><FlexButton style={{minHeight: 'auto', padding: '0px 30px'}} badge align='center' justify='center' primary text='TABLE FILTERS ARE ACTIVE'/></div>} */}
      <FlexBox ref={ref} row vstretch style={{ overflowY: 'hidden' }}>
        {headerColumnElems}
      </FlexBox>
    </div>
  );
});

function TableCell(props) {
  let { data, column, totalWeight, columns, index, basic, forceMobile, compact } = props;
  let content;
  if (data.component) {
    content = data.component;
  } else if (data.text) {
    content = data.text;
  }

  let style = {};
  if (!data.style) {
    style = {};
  } else {
    style = lodash.cloneDeep(data.style);
  }
  if (data.span) {
    if (data.span === 'all') {
      style.flex = '0 0 100%';
    } else if (!isNaN(data.span)) {
      style.flex = '0 0 ' + (data.span / totalWeight) * 100 + '%';
    }
  } else if (column.weight) {
    style.flex = '0 0 ' + (column.weight / totalWeight) * 100 + '%';
  }

  if (column.basis) {
    if (!isMobileOnly) {
      style.flexBasis = column.basis + 'px';
    }
    style.flexGrow = 1;
  }

  if (column.minWidth) {
    style.minWidth = column.minWidth + 'px';
  }

  if (column.justify && !(isMobileOnly || forceMobile)) {
    if (isMobileOnly) {
      style.justifyContent = 'flex-start';
    } else {
      style.justifyContent = column.justify;
    }
  }

  if (basic) {
    style.padding = '10px 0px';
    style.borderLeft = 'none';
  }

  if (compact) {
  }

  let columnHeaderElement;
  if (isMobileOnly || forceMobile) {
    style.flexDirection = 'row';
    style.alignItems = 'center';
    style.padding = '5px 15px';
    style.flex = '1';
    style.width = '100%';
    columnHeaderElement = (
      <div style={{ fontSize: '11px', minWidth: '150px' }} className={styles.tableCellColumnHeader}>
        {column.text}
      </div>
    );
  }

  let tableCellClasses = styles.tableCell;
  if (compact) {
    tableCellClasses += ` ${styles.cellCompact}`;
  }

  return (
    <div style={style} className={tableCellClasses}>
      {columnHeaderElement}
      {content}
    </div>
  );
}

function TableRow(props) {
  const { row, columns, onClick, totalWeight, basic, onClickRow, forceMobile, compact } = props;
  const { data, cells, sticky, stickyTop, stickyBottom } = row;
  const cellElems = cells.map((cell, index) => {
    return (
      <TableCell
        compact={compact}
        forceMobile={forceMobile}
        key={index + '_' + columns[index].key}
        index={index}
        totalWeight={totalWeight}
        data={cell}
        column={columns[index]}
        columns={columns}
        basic={basic}
      />
    );
  });

  const handleOnClick = (e) => {
    if (onClick) {
      onClick(e, row);
    } else if (onClickRow) {
      onClickRow(row);
    }
  };

  let style = row.style ? lodash.cloneDeep(row.style) : {};
  if (basic) {
    if (!style.background) {
      style.background = 'none';
    }
    style.borderRadius = 0;
    style.padding = '0px 0px';
  }
  if (isMobileOnly || forceMobile) {
    style.flexDirection = 'column';
    style.alignItems = 'flex-start';
    style.position = 'relative';
    style.padding = '10px 0px 0px 0px';
    style.marginTop = '10px';
    style.borderRadius = '5px';
    style.border = 'none';
    style.boxShadow = '0px 0px 0px 1px rgba(0,0,0,0.2) inset, 0px 1px 0px 1px rgba(0,0,0,0.05)';
  }

  if (sticky) {
    style.position = 'sticky';
    style.top = '0px';
    style.bottom = '0px';
    style.zIndex = 1;
  }

  if (stickyTop) {
    style.position = 'sticky';
    style.top = '0px';
    style.zIndex = 1;
  }

  if (stickyBottom) {
    style.position = 'sticky';
    style.bottom = '0px';
    style.zIndex = 1;
  }

  return (
    <div
      key={row.key}
      style={style}
      onClick={handleOnClick}
      className={`${styles.tableRow} ${basic ? styles.basicRow : ''} ${(onClickRow || onClick) && basic ? styles.basicRowHovered : ''} ${row.selected ? styles.rowSelected : ''}`}
    >
      {/* {selectorElem} */}
      {cellElems}
    </div>
  );
}

const TableContent = forwardRef((props, ref) => {
  const {
    rows,
    columns,
    sortColumnKey,
    performSort,
    sortColumnDirection,
    totalWeight,
    basic,
    noscroll,
    onClickRow,
    forceMobile,
    compact,
    loading,
  } = props;

  if (performSort && sortColumnKey) {
    let sortColumn = columns.find((col) => col.key === sortColumnKey);
    if (sortColumn) {
      let index = columns.indexOf(sortColumn);
      let modifier = sortColumnDirection === 'descending' ? -1 : 1;
      rows.sort((rowA, rowB) => {
        let cellA = rowA.cells[index];
        let cellB = rowB.cells[index];
        if (!cellA || !cellB) {
          return 0;
        }
        if (sortColumn.sort === 'numeric') {
          if (cellA.sortableContent !== null && cellB.sortableContent !== null) {
            return (cellA.sortableContent - cellB.sortableContent) * modifier;
          } else if (cellA.sortableContent && !cellB.sortableContent) {
            return 1 * modifier;
          } else if (!cellA.sortableContent && cellB.sortableContent) {
            return -1 * modifier;
          } else {
            return 0;
          }
        } else {
          if (cellA.sortableContent && cellB.sortableContent) {
            return cellA.sortableContent.localeCompare(cellB.sortableContent) * modifier;
          } else if (cellA.sortableContent && !cellB.sortableContent) {
            return 1 * modifier;
          } else if (!cellA.sortableContent && cellB.sortableContent) {
            return -1 * modifier;
          } else {
            return 0;
          }
        }
      });
    }
  }

  const rowElems = rows.map((row, index) => {
    return (
      <TableRow
        compact={compact}
        forceMobile={forceMobile}
        key={index}
        onClickRow={onClickRow}
        totalWeight={totalWeight}
        onClick={row.onClick}
        row={row}
        columns={columns}
        basic={basic}
      />
    );
  });

  let placeholder;
  if (rowElems.length === 0) {
    placeholder = (
      <div className={styles.tableContentPlaceholder}>
        {props.splash ? props.splash : !props.loading && <FlexLabel text="No Content to Display" />}
      </div>
    );
  }

  let tableContentStyle = {};
  if (basic) {
    tableContentStyle.background = 'none';
    tableContentStyle.overflowY = 'auto';
  }

  if (noscroll) {
    tableContentStyle.overflow = 'visible';
  }

  if (isMobileOnly || forceMobile) {
    tableContentStyle.padding = '5px';
    tableContentStyle.background = 'none';
  }

  return (
    <div ref={ref} style={tableContentStyle} id="selfie" className={styles.tableContent}>
      <>
        {loading && (
          <FlexBox align="center" justify="center" className={styles.tableLoader} fill>
            <AnimatedLoader />
          </FlexBox>
        )}
        <div style={tableContentStyle} className={styles.tableRowContainer}>
          {rowElems}
        </div>
        {placeholder}
      </>
    </div>
  );
});

function FlexTable(props) {
  let {
    infiniteScrollProps,
    row,
    columns,
    onClickRow,
    basic,
    footerContent,
    headerContent,
    banner,
    forceMobile = false,
    compact,
  } = props;
  const [sortColumnKey, setSortColumnKey] = useState(props.sortColumnKey);
  const [sortColumnDirection, setSortColumnDirection] = useState(null);
  const headerRef = useRef(null);
  const tableContentRef = useRef(null);

  useEffect(() => {
    if (sortColumnKey !== props.sortColumnKey) {
      setSortColumnKey(props.sortColumnKey);
      if (!props.sortColumnKey) {
        setSortColumnDirection(null);
      }
    }
  }, [props.sortColumnKey]);

  useEffect(() => {
    if (tableContentRef.current && headerRef.current) {
      tableContentRef.current.onscroll = (e) => {
        headerRef.current.scrollLeft = tableContentRef.current.scrollLeft;
      };
    }
  }, []);

  const handleChangeSortColumn = (key) => {
    let direction = null;
    let controlled = props.sortColumnKey;
    let resetSortDirection = false;
    if (sortColumnKey !== key) {
      resetSortDirection = true;
    }

    if (!controlled) {
      setSortColumnKey(key);
    }
    if (sortColumnDirection === null || resetSortDirection) {
      direction = 'asc';
      setSortColumnDirection('ascending');
    } else if (sortColumnDirection === 'ascending') {
      direction = 'desc';
      setSortColumnDirection('descending');
    } else if (sortColumnDirection === 'descending') {
      setSortColumnDirection('ascending');
      direction = 'asc';
    }

    if (props.onSortColumnChanged) {
      props.onSortColumnChanged({
        key: key,
        direction: direction,
      });
    }
  };

  let totalWeight = 0;
  columns.forEach((col) => {
    if (col.weight) {
      totalWeight += col.weight;
    } else {
      totalWeight += 1;
    }
  });

  let flexTableClasses = styles.flexTable;
  let tableStyle = {};
  if (props.style) {
    tableStyle = { ...props.style };
  }
  if (basic) {
    tableStyle.background = 'none';
    tableStyle.border = 'none';
    tableStyle.boxShadow = 'none';

    flexTableClasses += ` ${styles.basic}`;
  }

  if (props.noscroll) {
    tableStyle.overflow = 'visible';
  }

  let tableContent;
  if (infiniteScrollProps) {
    delete infiniteScrollProps['loader'];
    if (!infiniteScrollProps.dataLength) {
      infiniteScrollProps.dataLength = 1;
      console.error(
        'Infinite scrolling was attempted to be used in a FlexTable without dataLength property set.  Defensive measures were used to prevent a hard crash.',
      );
    }
    tableContent = (
      <div id={props.id} style={tableStyle} className={flexTableClasses}>
        {props.showHeader !== false && (
          <TableHeader
            ref={headerRef}
            forceMobile={forceMobile}
            banner={banner}
            basic={basic}
            columns={columns}
            totalWeight={totalWeight}
            sortColumnKey={sortColumnKey}
            changeSortColumn={handleChangeSortColumn}
            sortDirection={sortColumnDirection}
          />
        )}
        {headerContent}
        <div className={styles.infiniteScrollContainer}>
          <InfiniteScroll
            style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            scrollableTarget="selfie"
            {...infiniteScrollProps}
          >
            <TableContent
              performSort={props.performSort}
              compact={compact}
              ref={tableContentRef}
              forceMobile={forceMobile}
              onClickRow={onClickRow}
              basic={basic}
              {...props}
              sortColumnKey={sortColumnKey}
              sortColumnDirection={sortColumnDirection}
              totalWeight={totalWeight}
            />
          </InfiniteScroll>
        </div>
        {footerContent}
      </div>
    );
  } else {
    tableContent = (
      <div id={props.id} style={tableStyle} className={flexTableClasses}>
        {props.showHeader !== false && (
          <TableHeader
            ref={headerRef}
            forceMobile={forceMobile}
            banner={banner}
            basic={basic}
            columns={columns}
            totalWeight={totalWeight}
            sortColumnKey={sortColumnKey}
            changeSortColumn={handleChangeSortColumn}
            sortDirection={sortColumnDirection}
          />
        )}
        {headerContent}
        <TableContent
          performSort={props.performSort}
          compact={compact}
          ref={tableContentRef}
          forceMobile={forceMobile}
          onClickRow={onClickRow}
          basic={basic}
          noscroll={props.noscroll}
          totalWeight={totalWeight}
          {...props}
          sortColumnKey={sortColumnKey}
          sortColumnDirection={sortColumnDirection}
        />
        {footerContent}
      </div>
    );
  }

  if (props.guttered || props.topGutter || props.bottomGutter || props.leftGutter || props.rightGutter) {
    return (
      <FlexBox
        fill
        guttered={props.guttered}
        topGutter={props.topGutter}
        bottomGutter={props.bottomGutter}
        leftGutter={props.leftGutter}
        rightGutter={props.rightGutter}
      >
        {tableContent}
      </FlexBox>
    );
  } else {
    return tableContent;
  }
}

FlexTable.propTypes = {};

export default FlexTable;
